import { useEffect, useState, type FormEvent } from "react";
import { Check, Mail, X } from "lucide-react";

const API = (import.meta.env.VITE_CONTENT_API_URL ?? "").replace(/\/$/, "");
// Registration (approved-visitors) is a future phase whose /register route is intentionally not
// deployed yet (it's a public arbitrary-recipient SES mailer). Gate the UI on an explicit flag so
// enabling the content API alone never surfaces a register prompt that would hit an undeployed route.
export const registerEnabled =
  Boolean(API) && import.meta.env.VITE_REGISTER_ENABLED === "true";
const REG_KEY = "ir.registered.v1";

export interface Registered {
  email: string;
  ts: number;
}

export function getRegistered(): Registered | null {
  try {
    return JSON.parse(localStorage.getItem(REG_KEY) ?? "null") as Registered | null;
  } catch {
    return null;
  }
}
export const isRegistered = (): boolean => Boolean(getRegistered());

function setRegistered(email: string) {
  try {
    localStorage.setItem(REG_KEY, JSON.stringify({ email, ts: Date.now() }));
  } catch {
    // ignore
  }
  window.dispatchEvent(new Event("registered-change"));
}

/** Open the registration dialog from anywhere (mirrors openAsk/openFeedback). */
export function openRegister() {
  window.dispatchEvent(new Event("open-register"));
}

/** On load: if the URL carries a magic-link token, verify it and mark the visitor registered. */
export async function verifyMagicLink(): Promise<void> {
  const params = new URLSearchParams(window.location.search);
  const token = params.get("mlink");
  if (!token || !API) return;
  try {
    const res = await fetch(`${API}/register/verify`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ token }),
    });
    const data = (await res.json()) as { ok?: boolean; email?: string };
    if (res.ok && data.email) setRegistered(data.email);
  } catch {
    // best-effort
  } finally {
    params.delete("mlink");
    const q = params.toString();
    window.history.replaceState(
      {},
      "",
      window.location.pathname + (q ? `?${q}` : "") + window.location.hash,
    );
  }
}

export function RegisterDialog() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const onOpen = () => {
      setOpen(true);
      setSent(false);
      setErr(null);
    };
    window.addEventListener("open-register", onOpen);
    return () => window.removeEventListener("open-register", onOpen);
  }, []);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    setErr(null);
    try {
      const res = await fetch(`${API}/register`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (res.ok) setSent(true);
      else setErr("Couldn't send — check the address and retry.");
    } catch {
      setErr("Network error — please retry.");
    }
    setSending(false);
  };

  return (
    <div
      className="fb-overlay print:hidden"
      role="dialog"
      aria-modal="true"
      aria-label="Register"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) setOpen(false);
      }}
    >
      <div className="fb-modal">
        <div className="fb-modal-head">
          <div>
            <div className="fb-modal-title">Get more — register</div>
            <div className="fb-modal-sub mono">One-click email confirm. No password, no account.</div>
          </div>
          <button type="button" className="icon-btn" aria-label="Close" onClick={() => setOpen(false)}>
            <X size={18} />
          </button>
        </div>
        {sent ? (
          <div className="jfb-thanks">
            <span className="jfb-check">
              <Check size={24} />
            </span>
            <h4 className="jfb-thanks-h">Check your inbox</h4>
            <p className="jfb-thanks-p">
              I sent a confirmation link to <b>{email}</b>. Click it to finish — it expires in 1 hour.
            </p>
            <div className="jfb-actions">
              <button type="button" className="btn btn-sm btn-primary" onClick={() => setOpen(false)}>
                Done
              </button>
            </div>
          </div>
        ) : (
          <form className="jfb-form" onSubmit={submit}>
            <div className="jfb-field">
              <label className="jfb-label" htmlFor="reg-email">
                Email
              </label>
              <input
                id="reg-email"
                className="jfb-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
              />
            </div>
            {err && <p className="eng-err">{err}</p>}
            <div className="jfb-actions">
              <button type="button" className="btn btn-sm" onClick={() => setOpen(false)} disabled={sending}>
                Cancel
              </button>
              <button type="submit" className="btn btn-sm btn-primary" disabled={sending || !email.trim()}>
                <Mail size={14} /> {sending ? "Sending…" : "Send link"}
              </button>
            </div>
          </form>
        )}
        <div className="fb-modal-note">
          Registered visitors get additional questions and can opt into updates.
        </div>
      </div>
    </div>
  );
}
